import { useRef, useState, lazy, Suspense, useCallback, useEffect } from 'react';
import { useGraphStore } from '@/store/graphStore';
import { InfrastructureGraph } from '@/components/InfrastructureGraph';
import { LayerPanel } from '@/components/LayerPanel';
import { NodeDetailPanel } from '@/components/NodeDetailPanel';
import { ScanForm } from '@/components/ScanForm';
import type { InfrastructureGraphHandle } from '@/components/InfrastructureGraph';
import { useOverlayData } from '@/hooks/useOverlayData';
import { AdapterRegistry } from '@/adapters/index';
import { DarkAPIAdapter } from '@/adapters/DarkAPIAdapter';
import { WelcomePanel } from '@/components/WelcomePanel';
import { ScanResultsPanel } from '@/components/ScanResultsPanel';
import { LoginGate } from '@/components/LoginGate';
import { useAuthStore } from '@/store/authStore';
import { useAuthInit } from '@/hooks/useAuthInit';
import { NotificationPoller, NotificationToasts } from '@/components/NotificationSystem';
import { useNotificationStore } from '@/store/notificationStore';

// Register adapters once at module load (before any render)
AdapterRegistry.register(DarkAPIAdapter);

// Lazy-loaded panels (separate vendor chunks)
const GeoMapPanel = lazy(() =>
  import('@/components/GeoMapPanel').then((m) => ({ default: m.GeoMapPanel })),
);
const EmailHealthPanel = lazy(() =>
  import('@/components/EmailHealthPanel').then((m) => ({ default: m.EmailHealthPanel })),
);
const ChangeVelocityPanel = lazy(() =>
  import('@/components/ChangeVelocityPanel').then((m) => ({ default: m.ChangeVelocityPanel })),
);
const RiskIntelPanel = lazy(() =>
  import('@/components/RiskIntelPanel').then((m) => ({ default: m.RiskIntelPanel })),
);
const SSLSANPanel = lazy(() =>
  import('@/components/SSLSANPanel').then((m) => ({ default: m.SSLSANPanel })),
);
const SubdomainPanel = lazy(() =>
  import('@/components/SubdomainPanel').then((m) => ({ default: m.SubdomainPanel })),
);
const GdnsPanel = lazy(() =>
  import('@/components/GdnsPanel').then((m) => ({ default: m.GdnsPanel })),
);
const QueryAnalyticsPanel = lazy(() =>
  import('@/components/QueryAnalyticsPanel').then((m) => ({ default: m.QueryAnalyticsPanel })),
);
const DNSSECPanel = lazy(() =>
  import('@/components/DNSSECPanel').then((m) => ({ default: m.DNSSECPanel })),
);
const CacheInspectorPanel = lazy(() =>
  import('@/components/CacheInspectorPanel').then((m) => ({ default: m.CacheInspectorPanel })),
);
const SecurityOpsPanel = lazy(() =>
  import('@/components/SecurityOpsPanel').then((m) => ({ default: m.SecurityOpsPanel })),
);
const NetworkToolsPanel = lazy(() =>
  import('@/components/NetworkToolsPanel').then((m) => ({ default: m.NetworkToolsPanel })),
);
const TrafficIntelPanel = lazy(() =>
  import('@/components/TrafficIntelPanel').then((m) => ({ default: m.TrafficIntelPanel })),
);
const IntelToolsPanel = lazy(() =>
  import('@/components/IntelToolsPanel').then((m) => ({ default: m.IntelToolsPanel })),
);
const ChartsPanel = lazy(() =>
  import('@/components/ChartsPanel').then((m) => ({ default: m.ChartsPanel })),
);
const MonitorsPanel = lazy(() =>
  import('@/components/MonitorsPanel').then((m) => ({ default: m.MonitorsPanel })),
);
const DataExplorerPanel = lazy(() =>
  import('@/components/DataExplorerPanel').then((m) => ({ default: m.DataExplorerPanel })),
);
const SoftwarePanel = lazy(() =>
  import('@/components/SoftwarePanel').then((m) => ({ default: m.SoftwarePanel })),
);
const DomainIPPanel = lazy(() =>
  import('@/components/DomainIPPanel').then((m) => ({ default: m.DomainIPPanel })),
);
const DocsPanel = lazy(() =>
  import('@/components/DocsPanel').then((m) => ({ default: m.DocsPanel })),
);
const ApiRefPanel = lazy(() =>
  import('@/components/ApiRefPanel').then((m) => ({ default: m.ApiRefPanel })),
);
const AdvancedSearchPanel = lazy(() =>
  import('@/components/AdvancedSearchPanel').then((m) => ({ default: m.AdvancedSearchPanel })),
);
const ValuationPanel = lazy(() =>
  import('@/components/ValuationPanel').then((m) => ({ default: m.ValuationPanel })),
);
const BulkScannerPanel = lazy(() =>
  import('@/components/BulkScannerPanel').then((m) => ({ default: m.BulkScannerPanel })),
);
const RegistrarPanel = lazy(() =>
  import('@/components/RegistrarPanel').then((m) => ({ default: m.RegistrarPanel })),
);
const WalletPanel = lazy(() => import('@/components/WalletPanel'));
const HealthScorePanel = lazy(() => import('@/components/HealthScorePanel'));
const RPZFeedPanel = lazy(() => import('@/components/RPZFeedPanel'));
const PivotPanel = lazy(() => import('@/components/PivotPanel'));
const DNSTimelinePanel = lazy(() => import('@/components/DNSTimelinePanel'));
const WatchlistPanel = lazy(() => import('@/components/WatchlistPanel'));
const BulkCheckPanel = lazy(() => import('@/components/BulkCheckPanel'));
const MailSecurityPanel = lazy(() => import('@/components/MailSecurityPanel'));
const TunnelDetectionPanel = lazy(() => import('@/components/TunnelDetectionPanel'));
const DGAPanel = lazy(() => import('@/components/DGAPanel'));
const BGPHijackPanel = lazy(() => import('@/components/BGPHijackPanel'));
const SubdomainDiscoveryPanel = lazy(() => import('@/components/SubdomainDiscoveryPanel'));
const SecurityPosturePanel = lazy(() =>
  import('@/components/SecurityPosturePanel').then((m) => ({ default: m.SecurityPosturePanel })),
);
const ApiKeysPanel = lazy(() =>
  import('@/components/ApiKeysPanel').then((m) => ({ default: m.ApiKeysPanel })),
);

type ViewMode =
  | 'graph' | 'geo' | 'email' | 'velocity' | 'threats' | 'ssl' | 'subdomains'
  | 'gdns' | 'analytics' | 'dnssec' | 'cache' | 'secops' | 'nettools' | 'traffic' | 'intel'
  | 'charts' | 'monitors' | 'explorer' | 'software' | 'domain-ip' | 'docs' | 'api-ref'
  | 'search' | 'valuation' | 'bulk-scanner' | 'registrar' | 'wallet' | 'api-keys'
  | 'health-score' | 'rpz-feed' | 'pivot' | 'dns-timeline' | 'watchlists' | 'bulk-check'
  | 'mail-security' | 'tunnel-detection' | 'dga' | 'bgp-hijack' | 'subdomain-discovery'
  | 'posture';

interface NavItem { id: ViewMode; label: string; protected?: boolean }
interface NavGroup { label: string; items: NavItem[] }

// Standalone top-level views (rendered as plain buttons, not in a dropdown)
const STANDALONE_VIEWS: NavItem[] = [
  { id: 'graph', label: 'Graph' },
];

// Grouped views rendered as dropdown menus in the nav bar
const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Explore',
    items: [
      { id: 'geo',        label: 'Geo Map' },
      { id: 'domain-ip',  label: 'Domain↔IP' },
      { id: 'subdomains', label: 'Subdomains' },
    ],
  },
  {
    label: 'DNS',
    items: [
      { id: 'email',    label: 'Email Health' },
      { id: 'velocity', label: 'Changes' },
      { id: 'dnssec',   label: 'DNSSEC' },
      { id: 'nettools', label: 'Net Tools' },
    ],
  },
  {
    label: 'Threats',
    items: [
      { id: 'threats', label: 'Risk Intel' },
      { id: 'ssl',     label: 'SSL Pivot' },
      { id: 'secops',  label: 'SecOps' },
      { id: 'intel',            label: 'Intel Tools' },
      { id: 'bulk-scanner',     label: 'Bulk Scanner' },
      { id: 'tunnel-detection', label: 'Tunnel Detect' },
      { id: 'dga',              label: 'DGA Scorer' },
      { id: 'bgp-hijack',       label: 'BGP Hijack' },
      { id: 'posture',          label: 'Security Posture' },
    ],
  },
  {
    label: 'Server',
    items: [
      { id: 'gdns',      label: 'Server' },
      { id: 'analytics', label: 'Analytics' },
      { id: 'cache',     label: 'Cache' },
      { id: 'traffic',   label: 'Traffic Intel' },
    ],
  },
  {
    label: 'Data',
    items: [
      { id: 'charts',      label: 'Charts' },
      { id: 'explorer',    label: 'Data Explorer' },
      { id: 'search',      label: 'Advanced Search' },
      { id: 'valuation',   label: 'Valuations' },
      { id: 'dns-timeline', label: 'DNS Timeline' },
      { id: 'pivot',        label: 'Pivot Engine' },
      { id: 'health-score', label: 'Health Score' },
      { id: 'rpz-feed',     label: 'RPZ Feed' },
    ],
  },
  {
    label: 'Platform',
    items: [
      { id: 'monitors',    label: 'Monitors',    protected: true },
      { id: 'watchlists',  label: 'Watchlists',  protected: true },
      { id: 'bulk-check',  label: 'Bulk Check',  protected: true },
      { id: 'registrar',   label: 'Registrar',   protected: true },
      { id: 'wallet',      label: 'Wallet',      protected: true },
      { id: 'api-keys',    label: 'API Keys',    protected: true },
      { id: 'mail-security',       label: 'Mail Security' },
      { id: 'subdomain-discovery', label: 'Subdomains' },
      { id: 'software',   label: 'Software' },
      { id: 'docs',       label: 'Docs' },
      { id: 'api-ref',    label: 'API' },
    ],
  },
];

// ── Dropdown nav component ────────────────────────────────────────────────────

function NavDropdown({
  group, view, user, onSelect,
}: {
  group: NavGroup;
  view: ViewMode;
  user: { email: string } | null;
  onSelect: (id: ViewMode) => void;
}) {
  const [open, setOpen] = useState(false);
  const [showLoginFor, setShowLoginFor] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const isActive = group.items.some((i) => i.id === view);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setShowLoginFor(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleItemClick = (item: NavItem) => {
    if (item.protected && !user) {
      setShowLoginFor(item.id);
    } else {
      onSelect(item.id);
      setOpen(false);
      setShowLoginFor(null);
    }
  };

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        onClick={() => { setOpen((o) => !o); setShowLoginFor(null); }}
        style={{
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: 12, padding: '6px 12px',
          background: 'transparent', border: 'none',
          borderBottom: isActive ? '2px solid #58a6ff' : '2px solid transparent',
          color: isActive ? '#58a6ff' : '#8b949e',
          cursor: 'pointer', whiteSpace: 'nowrap',
          display: 'flex', alignItems: 'center', gap: 4,
        }}
      >
        {group.label}
        <span style={{ fontSize: 8, opacity: 0.6 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 100,
          background: '#161b22', border: '1px solid #30363d', borderRadius: 6,
          minWidth: 170, boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          padding: '4px 0',
        }}>
          {group.items.map((item) => (
            <button
              key={item.id}
              onClick={() => handleItemClick(item)}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                fontFamily: '"JetBrains Mono", monospace', fontSize: 12,
                padding: '7px 16px', background: 'transparent', border: 'none',
                color: showLoginFor === item.id ? '#d29922'
                  : view === item.id ? '#58a6ff' : '#c9d1d9',
                cursor: 'pointer',
                borderLeft: view === item.id ? '2px solid #58a6ff'
                  : showLoginFor === item.id ? '2px solid #d29922' : '2px solid transparent',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#21262d'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
            >
              {item.label}
              {item.protected && !user && (
                <span style={{ marginLeft: 6, opacity: 0.6, fontSize: 9 }}>🔒</span>
              )}
            </button>
          ))}

          {/* Inline login prompt when a locked item is clicked */}
          {showLoginFor && (
            <div style={{
              borderTop: '1px solid #30363d', margin: '4px 0 0',
              padding: '12px 16px',
            }}>
              <div style={{
                fontFamily: '"JetBrains Mono", monospace', fontSize: 11,
                color: '#8b949e', marginBottom: 10, lineHeight: 1.5,
              }}>
                Sign in to access{' '}
                <span style={{ color: '#e6edf3' }}>
                  {group.items.find((i) => i.id === showLoginFor)?.label}
                </span>
              </div>
              <a
                href="/api/auth/oauth/login"
                style={{
                  display: 'block', textAlign: 'center',
                  fontFamily: '"JetBrains Mono", monospace', fontSize: 11,
                  background: '#1f6feb', color: '#fff', borderRadius: 6,
                  padding: '7px 0', textDecoration: 'none', fontWeight: 600,
                }}
              >
                Sign in with AfterDark
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PanelFallback({ label }: { label: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        fontFamily: '"JetBrains Mono", monospace',
        color: '#8b949e',
        fontSize: 13,
      }}
    >
      Loading {label}...
    </div>
  );
}

export default function App() {
  const graphRef = useRef<InfrastructureGraphHandle | null>(null);
  const [view, setView] = useState<ViewMode>('graph');
  useOverlayData(graphRef);
  useAuthInit();

  const storeClearGraph = useGraphStore((s) => s.clearGraph);
  const goHome = useCallback(() => {
    storeClearGraph();
    setView('graph');
  }, [storeClearGraph]);

  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const unackedAlarms = useNotificationStore((s) => s.unackedAlarms);

  return (
    <div className="h-full w-full flex flex-col" style={{ background: '#0a0e14' }}>
      {/* ── Top bar ──────────────────────────────────────────────────────── */}
      <header
        className="flex items-center gap-4 px-4 py-2 flex-shrink-0 border-b"
        style={{ borderColor: '#30363d', background: '#0d1117' }}
      >
        {/* Logo — click to go home */}
        <button
          onClick={goHome}
          className="font-mono font-bold text-base flex-shrink-0"
          style={{ color: '#58a6ff', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        >
          DNSScience.io
        </button>

        {/* Search — centered */}
        <div className="flex-1 flex justify-center">
          <ScanForm graphRef={graphRef} />
        </div>

        {/* Alarm bell */}
        {unackedAlarms > 0 && (
          <button
            onClick={() => setView('monitors')}
            style={{
              background: '#f8514918', border: '1px solid #f8514944', borderRadius: 6,
              color: '#f85149', cursor: 'pointer', fontSize: 11, padding: '3px 10px',
              fontFamily: '"JetBrains Mono", monospace', flexShrink: 0,
            }}
          >
            ⚠ {unackedAlarms} alarm{unackedAlarms !== 1 ? 's' : ''}
          </button>
        )}

        {/* Auth — sign in or user+logout */}
        <div style={{ flexShrink: 0 }}>
          {user ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ color: '#8b949e', fontSize: 11, fontFamily: '"JetBrains Mono", monospace' }}>
                {user.email}
              </span>
              <button
                onClick={logout}
                style={{
                  background: 'none', border: '1px solid #30363d', borderRadius: 4,
                  color: '#8b949e', cursor: 'pointer', fontSize: 11,
                  fontFamily: '"JetBrains Mono", monospace', padding: '3px 10px',
                }}
              >
                logout
              </button>
            </div>
          ) : (
            <a
              href="/api/auth/oauth/login"
              style={{
                background: 'none', border: '1px solid #30363d', borderRadius: 4,
                color: '#58a6ff', cursor: 'pointer', fontSize: 11,
                fontFamily: '"JetBrains Mono", monospace', padding: '3px 10px',
                textDecoration: 'none',
              }}
            >
              sign in
            </a>
          )}
        </div>
      </header>

      {/* ── Nav bar ──────────────────────────────────────────────────────── */}
      <div
        className="flex-shrink-0 border-b"
        style={{ borderColor: '#21262d', background: '#0d1117' }}
      >
        <div className="flex items-center">
          {/* Standalone views */}
          {STANDALONE_VIEWS.map((item) => (
            <button
              key={item.id}
              onClick={() => setView(item.id)}
              style={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: 12, padding: '6px 12px', flexShrink: 0,
                background: 'transparent', border: 'none',
                borderBottom: view === item.id ? '2px solid #58a6ff' : '2px solid transparent',
                color: view === item.id ? '#58a6ff' : '#8b949e',
                cursor: 'pointer', whiteSpace: 'nowrap',
              }}
            >
              {item.label}
            </button>
          ))}

          {/* Dropdown groups */}
          {NAV_GROUPS.map((group) => (
            <NavDropdown
              key={group.label}
              group={group}
              view={view}
              user={user}
              onSelect={setView}
            />
          ))}
        </div>
      </div>

      {/* ── Notification system ──────────────────────────────────────────── */}
      <NotificationPoller />
      <NotificationToasts />

      {/* ── Main canvas ──────────────────────────────────────────────────── */}
      <main className="flex-1 overflow-hidden relative">
        {view === 'graph' && (
          <>
            <LayerPanel />
            <ScanResultsPanel />
            <InfrastructureGraph ref={graphRef} />
            <NodeDetailPanel />
            <WelcomePanel />
          </>
        )}

        {view === 'geo' && (
          <Suspense fallback={<PanelFallback label="geo map" />}>
            <GeoMapPanel />
          </Suspense>
        )}

        {view === 'email' && (
          <Suspense fallback={<PanelFallback label="email health" />}>
            <EmailHealthPanel />
          </Suspense>
        )}

        {view === 'velocity' && (
          <Suspense fallback={<PanelFallback label="change velocity" />}>
            <ChangeVelocityPanel />
          </Suspense>
        )}

        {view === 'threats' && (
          <Suspense fallback={<PanelFallback label="risk intelligence" />}>
            <RiskIntelPanel />
          </Suspense>
        )}

        {view === 'ssl' && (
          <Suspense fallback={<PanelFallback label="SSL pivot" />}>
            <SSLSANPanel />
          </Suspense>
        )}

        {view === 'subdomains' && (
          <Suspense fallback={<PanelFallback label="subdomain discovery" />}>
            <SubdomainPanel />
          </Suspense>
        )}

        {view === 'gdns' && (
          <Suspense fallback={<PanelFallback label="server dashboard" />}>
            <GdnsPanel />
          </Suspense>
        )}

        {view === 'analytics' && (
          <Suspense fallback={<PanelFallback label="query analytics" />}>
            <QueryAnalyticsPanel />
          </Suspense>
        )}

        {view === 'dnssec' && (
          <Suspense fallback={<PanelFallback label="DNSSEC" />}>
            <DNSSECPanel />
          </Suspense>
        )}

        {view === 'cache' && (
          <Suspense fallback={<PanelFallback label="cache inspector" />}>
            <CacheInspectorPanel />
          </Suspense>
        )}

        {view === 'secops' && (
          <Suspense fallback={<PanelFallback label="security ops" />}>
            <SecurityOpsPanel />
          </Suspense>
        )}

        {view === 'nettools' && (
          <Suspense fallback={<PanelFallback label="network tools" />}>
            <NetworkToolsPanel />
          </Suspense>
        )}

        {view === 'traffic' && (
          <Suspense fallback={<PanelFallback label="traffic intel" />}>
            <TrafficIntelPanel />
          </Suspense>
        )}

        {view === 'intel' && (
          <Suspense fallback={<PanelFallback label="intel tools" />}>
            <IntelToolsPanel />
          </Suspense>
        )}

        {view === 'charts' && (
          <Suspense fallback={<PanelFallback label="charts" />}>
            <ChartsPanel />
          </Suspense>
        )}

        {view === 'monitors' && (
          <LoginGate feature="Monitors">
            <Suspense fallback={<PanelFallback label="monitors" />}>
              <MonitorsPanel />
            </Suspense>
          </LoginGate>
        )}

        {view === 'api-keys' && (
          <LoginGate feature="API Keys">
            <Suspense fallback={<PanelFallback label="api keys" />}>
              <ApiKeysPanel />
            </Suspense>
          </LoginGate>
        )}

        {view === 'explorer' && (
          <Suspense fallback={<PanelFallback label="data explorer" />}>
            <DataExplorerPanel />
          </Suspense>
        )}

        {view === 'software' && (
          <Suspense fallback={<PanelFallback label="software" />}>
            <SoftwarePanel />
          </Suspense>
        )}

        {view === 'domain-ip' && (
          <Suspense fallback={<PanelFallback label="domain↔IP" />}>
            <DomainIPPanel />
          </Suspense>
        )}

        {view === 'docs' && (
          <Suspense fallback={<PanelFallback label="docs" />}>
            <DocsPanel />
          </Suspense>
        )}

        {view === 'api-ref' && (
          <Suspense fallback={<PanelFallback label="API reference" />}>
            <ApiRefPanel />
          </Suspense>
        )}
        {view === 'search' && (
          <Suspense fallback={<PanelFallback label="Advanced Search" />}>
            <AdvancedSearchPanel />
          </Suspense>
        )}
        {view === 'valuation' && (
          <Suspense fallback={<PanelFallback label="Valuations" />}>
            <ValuationPanel />
          </Suspense>
        )}
        {view === 'bulk-scanner' && (
          <Suspense fallback={<PanelFallback label="Bulk Scanner" />}>
            <BulkScannerPanel />
          </Suspense>
        )}
        {view === 'registrar' && (
          <LoginGate feature="Registrar">
            <Suspense fallback={<PanelFallback label="registrar" />}>
              <RegistrarPanel />
            </Suspense>
          </LoginGate>
        )}
        {view === 'wallet' && (
          <LoginGate feature="Wallet">
            <Suspense fallback={<PanelFallback label="wallet" />}>
              <WalletPanel />
            </Suspense>
          </LoginGate>
        )}
        {view === 'health-score' && (
          <Suspense fallback={<PanelFallback label="health score" />}>
            <HealthScorePanel />
          </Suspense>
        )}
        {view === 'rpz-feed' && (
          <Suspense fallback={<PanelFallback label="RPZ feed" />}>
            <RPZFeedPanel />
          </Suspense>
        )}
        {view === 'pivot' && (
          <Suspense fallback={<PanelFallback label="pivot engine" />}>
            <PivotPanel />
          </Suspense>
        )}
        {view === 'dns-timeline' && (
          <Suspense fallback={<PanelFallback label="DNS timeline" />}>
            <DNSTimelinePanel />
          </Suspense>
        )}
        {view === 'watchlists' && (
          <LoginGate feature="Watchlists">
            <Suspense fallback={<PanelFallback label="watchlists" />}>
              <WatchlistPanel />
            </Suspense>
          </LoginGate>
        )}
        {view === 'bulk-check' && (
          <LoginGate feature="Bulk Check">
            <Suspense fallback={<PanelFallback label="bulk check" />}>
              <BulkCheckPanel />
            </Suspense>
          </LoginGate>
        )}
        {view === 'mail-security' && (
          <Suspense fallback={<PanelFallback label="mail security" />}>
            <MailSecurityPanel />
          </Suspense>
        )}
        {view === 'tunnel-detection' && (
          <Suspense fallback={<PanelFallback label="tunnel detection" />}>
            <TunnelDetectionPanel />
          </Suspense>
        )}
        {view === 'dga' && (
          <Suspense fallback={<PanelFallback label="DGA scorer" />}>
            <DGAPanel />
          </Suspense>
        )}
        {view === 'bgp-hijack' && (
          <Suspense fallback={<PanelFallback label="BGP hijack" />}>
            <BGPHijackPanel />
          </Suspense>
        )}
        {view === 'subdomain-discovery' && (
          <Suspense fallback={<PanelFallback label="subdomain discovery" />}>
            <SubdomainDiscoveryPanel />
          </Suspense>
        )}
        {view === 'posture' && (
          <Suspense fallback={<PanelFallback label="security posture" />}>
            <SecurityPosturePanel />
          </Suspense>
        )}
      </main>
    </div>
  );
}
